import { useCallback, useState } from 'react';

/**
 * "Full width" per document, on this device (owner, 14.10.2026 — Notion's
 * per-page toggle): the reading column drops its 760 px measure and uses the
 * pane's width, side padding kept. Default standard. One small map
 * `{ [documentId]: true }` in localStorage; a document back on standard width
 * leaves the map, so it never grows with pages nobody widened.
 */
const KEY = 'documents-full-width';

function readMap() {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function isFullWidth(documentId) {
  return readMap()[documentId] === true;
}

export function setFullWidth(documentId, on) {
  const map = readMap();
  if (on) map[documentId] = true;
  else delete map[documentId];
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable — the choice lasts while the tab is open */
  }
}

/** `[fullWidth, toggle]` for one document. */
export default function useFullWidth(documentId) {
  const [state, setState] = useState(() => ({ id: documentId, on: isFullWidth(documentId) }));
  // Same component, another document: re-read synchronously (no flash of the old width).
  const current = state.id === documentId ? state : { id: documentId, on: isFullWidth(documentId) };
  if (current !== state) setState(current);
  const toggle = useCallback(() => {
    const on = !isFullWidth(documentId);
    setFullWidth(documentId, on);
    setState({ id: documentId, on });
  }, [documentId]);
  return [current.on, toggle];
}
